import { RedirectToSignIn, SignedIn, SignedOut } from '@clerk/nextjs';
import '@knocklabs/react/dist/index.css';
import type { WithFeatureFlags } from '@utils/featureFlags';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import RUMUser from '~/components/analytics/RUMUser';
import { AssistantModal } from '~/components/assistant-ui/AssistantModal';
import { DraggableIntercomLauncher } from '~/components/intercom/DraggableIntercomLauncher';
import { SideNavContainer } from '~/components/layout/';
import { classNames } from '~/components/utils';
import useIntercomBoot from '~/hooks/useIntercomBoot';
import { useKnockNotifications } from '~/hooks/useKnockNotifications';
import { FeatureFlagProvider } from '~/react_context/FeatureFlagContext';
import { UserProvider } from '~/react_context/UserContext';

const ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools').then((mod) => mod.ReactQueryDevtools), { ssr: false });

type DefaultLayoutProps = {
	children: ReactNode;
	pageTitle?: string;
	backgroundColor?: string;
} & WithFeatureFlags;

function Contents({ children }: { children: ReactNode }) {
	// NB: these need to be within UserProvider
	useIntercomBoot({ intercomAppId: process.env.NEXT_PUBLIC_INTERCOM_APP_ID! });
	useKnockNotifications();

	return <>{children}</>;
}

export const DefaultLayout = ({ children, pageTitle, featureFlags, backgroundColor = 'bg-white' }: DefaultLayoutProps) => {
	const { asPath: pathname } = useRouter();
	return (
		<>
			<Head>
				<title>{pageTitle ?? 'Glazier'}</title>
				<link rel="icon" href="/favicon.ico" />
				{/* Responsive Meta Tag for proper scaling */}
				<meta name="viewport" content="initial-scale=1, width=device-width" />
			</Head>
			{/* @ts-ignore Clerk Upgrade */}
			<SignedIn>
				<UserProvider>
					<FeatureFlagProvider featureFlags={featureFlags}>
						{<RUMUser />}
						<main className={classNames('h-screen', backgroundColor)}>
							<div className="h-full overflow-auto">
								<SideNavContainer pathname={pathname}>
									<Contents>{children}</Contents>
								</SideNavContainer>
							</div>
						</main>
						<AssistantModal />
						<DraggableIntercomLauncher />
					</FeatureFlagProvider>
				</UserProvider>
			</SignedIn>
			{/* @ts-ignore Clerk Upgrade */}
			<SignedOut>
				{/* @ts-ignore Clerk Upgrade */}
				<RedirectToSignIn />
			</SignedOut>

			<ReactQueryDevtools position="bottom-right" initialIsOpen={false} />
		</>
	);
};
